import jsonpatch from "fast-json-patch";

const { applyPatch, deepClone } = jsonpatch;

import type { CompiledJsonTransform } from "../JsonMigration";
import type {
  AtomData,
  ChangeLog,
  ChangelLogMetaInfo,
  VersionedAtomData,
} from "../VersionAtom";
import { applyDefaultsForMissingKeys } from "./normalizeVersionedAtomPayload";

/**
 * Advance an accumulator document across schema eras by applying the compiled
 * per-step transforms. `migrations[i]` transforms `v(i+1)` -> `v(i+2)`, so to
 * step from era `e` to `e+1` (both >= 1) we apply `migrations[e-1]`. The
 * `v0 -> v1` gap has no structural transform (it is only default back-fill),
 * so era `0 -> 1` applies nothing.
 */
function advanceEra(
  doc: AtomData,
  fromEra: number,
  toEra: number,
  migrations: CompiledJsonTransform[],
): AtomData {
  let out = doc;
  for (let e = fromEra; e < toEra; e++) {
    const idx = e - 1; // transform v_e -> v_{e+1}
    const transform = migrations[idx];
    if (idx >= 0 && transform) {
      out = transform(out);
    }
  }
  return out;
}

/**
 * Era-aware ordered fold of a versioned atom's history. `applyOne` applies a
 * single changelog entry's patch onto the accumulator. Between entries, when a
 * later entry's authored era is greater than the accumulator's current era, the
 * accumulator is migrated forward so the entry's (era-shaped) patch applies to a
 * matching document. After all entries, the accumulator is migrated up to the
 * current version (`migrations.length + 1`).
 *
 * Callers own how a patch is applied (`applyOne`): the at-rest read paths clone
 * each patch first (their values arrive as plain JSON), while in-process callers
 * apply patches directly so non-JSON values such as `Date` survive the fold.
 *
 * Never call this with an empty `history`: it would advance an empty document
 * from era 0 to the current version, fabricating `addField` defaults and running
 * `custom` transforms against `{}`.
 */
export function foldHistoryEraAware<DataType extends AtomData>(
  history: Array<ChangeLog<DataType>>,
  migrations: CompiledJsonTransform[],
  applyOne: (acc: DataType, entry: ChangeLog<DataType>) => DataType,
): DataType {
  const currentVersion = migrations.length + 1;
  let acc = {} as DataType;
  let currentEra = history[0]?.schemaVersion ?? 0;

  for (const entry of history) {
    const entryEra = entry.schemaVersion ?? currentEra;
    if (entryEra > currentEra) {
      acc = advanceEra(acc, currentEra, entryEra, migrations) as DataType;
      currentEra = entryEra;
    }
    acc = applyOne(acc, entry);
  }

  if (currentEra < currentVersion) {
    acc = advanceEra(acc, currentEra, currentVersion, migrations) as DataType;
  }

  return acc;
}

/**
 * Versioned atoms have the latest value included now.
 * Still used when computing snapshot state from __history (history already
 * filtered by cutoff in the provide layer).
 *
 * @param defaults - Optional JSDoc `@default` map for the data interface; used to
 *   back-fill fields missing from older stored patch history.
 * @param migrations - Optional compiled per-version transforms (one per version
 *   step; `migrations[i]` transforms `v(i+1)` -> `v(i+2)`). When omitted/empty
 *   the fold is byte-for-byte identical to the pre-versioning behavior.
 */
export function calculateLatestVersionedAtomValue<
  DataType extends AtomData = AtomData,
>(
  history: Array<ChangeLog<DataType>>,
  defaults?: Record<string, unknown>,
  migrations?: CompiledJsonTransform[],
): DataType & ChangelLogMetaInfo {
  if (history.length === 0) {
    const empty = {} as DataType & ChangelLogMetaInfo;
    return defaults
      ? (applyDefaultsForMissingKeys(
          empty as unknown as Record<string, unknown>,
          defaults,
        ) as DataType & ChangelLogMetaInfo)
      : empty;
  }

  const latest =
    migrations && migrations.length > 0
      ? (foldHistoryEraAware(
          history,
          migrations,
          (acc, { changes }) => applyPatch(acc, deepClone(changes)).newDocument,
        ) as DataType & ChangelLogMetaInfo)
      : history.reduce(
          (acc, { changes }) => {
            return applyPatch(acc, deepClone(changes)).newDocument;
          },
          {} as DataType & ChangelLogMetaInfo,
        );

  const latestHistoryElement = history.at(-1);
  if (!latestHistoryElement) {
    const empty = {} as DataType & ChangelLogMetaInfo;
    return defaults
      ? (applyDefaultsForMissingKeys(
          empty as unknown as Record<string, unknown>,
          defaults,
        ) as DataType & ChangelLogMetaInfo)
      : empty;
  }

  latest.date = latestHistoryElement.date;
  latest.author = latestHistoryElement.author;

  return defaults
    ? (applyDefaultsForMissingKeys(
        latest as unknown as Record<string, unknown>,
        defaults,
      ) as DataType & ChangelLogMetaInfo)
    : latest;
}

export function calculateVersionedAtomValueAtCutoff<
  DataType extends AtomData = AtomData,
>(
  value: VersionedAtomData<DataType>,
  cutoffDate: Date,
  defaults?: Record<string, unknown>,
  migrations?: CompiledJsonTransform[],
): VersionedAtomData<DataType> {
  const cutoffMs = cutoffDate.getTime();
  if (Number.isNaN(cutoffMs)) {
    return value;
  }

  const history = Array.isArray(value.__history) ? value.__history : [];
  if (history.length === 0) {
    return value;
  }

  const filteredHistory = history.filter((entry) => {
    if (!entry?.date) return false;
    const entryMs = new Date(entry.date).getTime();
    if (Number.isNaN(entryMs)) return false;
    return entryMs <= cutoffMs;
  });

  let doc: DataType;
  if (filteredHistory.length === 0) {
    // The cutoff precedes every entry: the atom did not exist yet. Folding an
    // empty history era-aware would still advance era 0 -> currentVersion on
    // `{}`, fabricating `addField` defaults for a point in time the atom had no
    // value at — and running `custom` transforms against a document whose
    // fields they may dereference. Match the no-migrations branch exactly.
    doc = {} as DataType;
  } else if (migrations && migrations.length > 0) {
    doc = foldHistoryEraAware(
      filteredHistory,
      migrations,
      (acc, entry) =>
        applyPatch(acc, deepClone(entry.changes), false, true).newDocument,
    );
  } else {
    doc = {} as DataType;
    for (const entry of filteredHistory) {
      doc = applyPatch(doc, deepClone(entry.changes), false, true).newDocument;
    }
  }

  if (defaults) {
    doc = applyDefaultsForMissingKeys(
      doc as unknown as Record<string, unknown>,
      defaults,
    ) as DataType;
  }

  const latestEntry = filteredHistory.at(-1);
  const latestMeta = latestEntry
    ? { author: latestEntry.author, date: latestEntry.date }
    : value.__latestMeta;

  return {
    ...doc,
    __latestMeta: latestMeta,
    __history: filteredHistory,
  } as VersionedAtomData<DataType>;
}
